import { useState, useEffect } from 'react';
import { Typography, Box, Button, Radio, RadioGroup, FormControlLabel, FormControl, Divider, TextField } from '@mui/material';
import { GuestGroup, GuestRSVP, GroupRSVP } from '../services/api';
import { buttonMixins, typographyMixins, formMixins } from '../styles/mixins';

interface RSVPFormProps {
  guestGroup: GuestGroup;
  onSubmit: (groupId: string, rsvps: Record<string, GuestRSVP>, groupRSVP: GroupRSVP) => void;
  onBack: () => void;
  initialEmail?: string;
}

const EVENTS = [
  {
    id: 'contactInfo',
    title: 'CONTACT INFORMATION',
    description: 'What\'s a good email address where we can reach you with updates?',
    isContactInfo: true
  },
  {
    id: 'welcomeParty',
    title: 'WELCOME PARTY',
    description: "Join us for a welcome party at the Cactus Jack's Saloon in Evergreen to start the weekend celebrations!",
    details: [
      "Cactus Jack's Saloon",
      'Friday, August 1st',
      '6:00 PM - 8:00 PM',
      'Casual Attire',
      "We will be serving light hors d'oeuvres, beer and wine."
    ]
  },
  {
    id: 'wedding',
    title: 'WEDDING',
    description: 'Please join us for our wedding ceremony and reception',
    details: [
      'Echo Mountain',
      '19285 CO-103',
      'Idaho Springs, CO 80452',
      'Saturday, August 2nd',
      '4:00 PM - 10:00 PM',
      'Cocktail Attire'
    ]
  },
  {
    id: 'transportation',
    title: 'TRANSPORTATION',
    description: 'We are providing a shuttle service that will pick up/drop off guests.',
    details: [
      'The shuttle pickup will be at the:',
      'Hogback Park-n-Ride (Dinosaur Lots)',
      'on the day of our wedding.',
      'More details on this will follow as we get RSVPs!'
    ],
    isTransportation: true
  }
];

export default function RSVPForm({ guestGroup, onSubmit, onBack, initialEmail = '' }: RSVPFormProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [rsvps, setRsvps] = useState<Record<string, GuestRSVP>>(() => {
    // Initialize with default values for each guest or use existing RSVP data if available
    const initialRsvps: Record<string, GuestRSVP> = {};
    guestGroup.guests.forEach(guest => {
      // If the guest has existing RSVP data, use it
      if (guest.rsvp) {
        initialRsvps[guest.guestId] = guest.rsvp;
      } else {
        // Otherwise use default values
        initialRsvps[guest.guestId] = {
          welcomeParty: { attending: false },
          wedding: { attending: false },
          additionalEvents: {
            needsLodging: false,
            needsTransportation: false
          },
          transportation: {
            option: ''
          }
        };
      }
    });
    return initialRsvps;
  });
  
  const [groupRSVP, setGroupRSVP] = useState<GroupRSVP>({
    email: initialEmail
  });
  
  // Update email if initialEmail changes (e.g., when editing an existing RSVP)
  useEffect(() => {
    if (initialEmail) {
      setGroupRSVP(prev => ({ ...prev, email: initialEmail }));
    }
  }, [initialEmail]);
  
  const [emailError, setEmailError] = useState<string | null>(null);

  const handleAttendanceChange = (guestId: string, eventId: keyof Pick<GuestRSVP, 'welcomeParty' | 'wedding'>, attending: boolean) => {
    setRsvps(prev => ({
      ...prev,
      [guestId]: {
        ...prev[guestId],
        [eventId]: { attending }
      }
    }));
  };

  const handleLogisticsChange = (guestId: string, field: 'needsLodging' | 'needsTransportation', value: boolean) => {
    setRsvps(prev => {
      const currentRsvp = prev[guestId];
      const updatedRsvp: GuestRSVP = {
        welcomeParty: currentRsvp.welcomeParty,
        wedding: currentRsvp.wedding,
        additionalEvents: {
          needsLodging: field === 'needsLodging' ? value : currentRsvp.additionalEvents.needsLodging,
          needsTransportation: field === 'needsTransportation' ? value : currentRsvp.additionalEvents.needsTransportation
        },
        transportation: currentRsvp.transportation
      };
      
      return {
        ...prev,
        [guestId]: updatedRsvp
      };
    });
  };

  const handleTransportationChange = (guestId: string, option: string) => {
    setRsvps(prev => {
      const currentRsvp = prev[guestId];
      return {
        ...prev,
        [guestId]: {
          ...currentRsvp,
          transportation: {
            option
          }
        }
      };
    });
  };

  const handleGroupEmailChange = (email: string) => {
    setGroupRSVP(prev => ({
      ...prev,
      email
    }));
    
    // Validate email
    if (email && !validateEmail(email)) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError(null);
    }
  };
  
  const validateEmail = (email: string): boolean => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const currentEvent = EVENTS[currentStep];
  const isLastStep = currentStep === EVENTS.length - 1;

  const handleNext = () => {
    if (isLastStep) {
      onSubmit(guestGroup.groupId, rsvps, groupRSVP);
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep === 0) {
      onBack();
    } else {
      setCurrentStep(prev => prev - 1);
    }
  };

  return (
    <Box sx={formMixins.formContainer}>
      <Typography variant="body1" sx={{ mb: 2, fontWeight: 'normal' }}>
        {currentStep + 1}/{EVENTS.length} Events
      </Typography>

      <Typography 
        variant="h4" 
        sx={{ 
          ...typographyMixins.pageTitle,
          textTransform: 'uppercase',
          letterSpacing: 1
        }}
      >
        {currentEvent.id === 'wedding' ? 'WEDDING' : currentEvent.title}
      </Typography>

      {currentEvent.id === 'wedding' && (
        <>
          <Typography variant="body1" sx={{ mb: 1, fontWeight: 'bold' }}>
            Echo Mountain
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            19285 CO-103
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            Idaho Springs, CO 80452
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            Saturday, August 2nd
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            4:00 PM - 10:00 PM
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Dress: Cocktail Attire
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            The wedding ceremony and reception is an adult only event.
          </Typography>
          <Typography variant="body1" sx={{ mb: 4 }}>
            Please plan to arrive by 3:30 PM for the ceremony.
          </Typography>
          <Typography variant="body1" sx={{ mb: 4 }}>
            For more information, check out our Schedule page.
          </Typography>
        </>
      )}

      {currentEvent.id !== 'wedding' && (
        <>
          <Typography variant="body1" sx={{ mb: 4 }}>
            {currentEvent.description}
          </Typography>

          {currentEvent.details?.map((detail, index) => (
            <Typography key={index} variant="body2" sx={{ mb: 1 }}>
              {detail}
            </Typography>
          ))}
        </>
      )}

      <Divider sx={{ my: 4 }} />

      <Box sx={formMixins.formSection}>
        {currentEvent.isContactInfo ? (
          <Box sx={{ width: '100%' }}>
            <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 1 }}>
              Email Address
            </Typography>
            <TextField
              fullWidth
              type="email"
              placeholder="Email address for updates"
              variant="outlined"
              value={groupRSVP.email || ''}
              onChange={(e) => handleGroupEmailChange(e.target.value)}
              error={!!emailError}
              helperText={emailError}
              sx={formMixins.formField}
            />
          </Box>
        ) : currentEvent.isTransportation ? (
          <>
            {guestGroup.guests.map(guest => {
              // Only show transportation options for guests attending the wedding
              if (!rsvps[guest.guestId]?.wedding?.attending) return null;
              
              return (
                <Box key={guest.guestId} sx={{ mb: 4 }}>
                  <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 2 }}>
                    Will {guest.firstName} be taking the shuttle?
                  </Typography>
                  
                  <FormControl component="fieldset" sx={{ width: '100%' }}>
                    <RadioGroup
                      value={rsvps[guest.guestId]?.transportation?.option || ''}
                      onChange={(e) => handleTransportationChange(guest.guestId, e.target.value)}
                    >
                      <FormControlLabel 
                        value="bus" 
                        control={<Radio />} 
                        label="Taking the shuttle!" 
                      />
                      <FormControlLabel 
                        value="own" 
                        control={<Radio />} 
                        label="I have my own transportation" 
                      />
                    </RadioGroup>
                  </FormControl>
                  <Divider sx={{ mt: 3 }} />
                </Box>
              );
            })}
            {/* If no guests are attending the wedding, show a message */}
            {!guestGroup.guests.some(guest => rsvps[guest.guestId]?.wedding?.attending) && (
              <Typography variant="body1" sx={{ mb: 2 }}>
                No transportation options needed as no guests are attending the wedding.
              </Typography>
            )}
          </>
        ) : (
          guestGroup.guests.map(guest => (
            <Box key={guest.guestId} sx={{ mb: 4 }}>
              {currentEvent.id === 'logistics' ? (
              <>
                <Box sx={{ mb: 3, width: '100%' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                      Will you need lodging?
                    </Typography>
                    <FormControl component="fieldset">
                      <RadioGroup
                        row
                        value={rsvps[guest.guestId]?.additionalEvents?.needsLodging ?? false}
                        onChange={(e) => handleLogisticsChange(guest.guestId, 'needsLodging', e.target.value === 'true')}
                        sx={{ gap: 2 }}
                      >
                        <FormControlLabel value={true} control={<Radio />} label="Yes" />
                        <FormControlLabel value={false} control={<Radio />} label="No" />
                      </RadioGroup>
                    </FormControl>
                  </Box>
                </Box>

                <Box sx={{ width: '100%', mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                      Will you need transportation?
                    </Typography>
                    <FormControl component="fieldset">
                      <RadioGroup
                        row
                        value={rsvps[guest.guestId]?.additionalEvents?.needsTransportation ?? false}
                        onChange={(e) => handleLogisticsChange(guest.guestId, 'needsTransportation', e.target.value === 'true')}
                        sx={{ gap: 2 }}
                      >
                        <FormControlLabel value={true} control={<Radio />} label="Yes" />
                        <FormControlLabel value={false} control={<Radio />} label="No" />
                      </RadioGroup>
                    </FormControl>
                  </Box>
                </Box>

              </>
            ) : (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between', 
                alignItems: { xs: 'flex-start', sm: 'center' }, 
                width: '100%' 
              }}>
                <Typography variant="body1" sx={{ fontWeight: 'medium', mb: { xs: 1, sm: 0 } }}>
                  {guest.firstName} {guest.lastName}
                </Typography>
                <FormControl component="fieldset">
                  <RadioGroup
                    row
                    value={
                      currentEvent.id === 'welcomeParty' || currentEvent.id === 'wedding' 
                        ? rsvps[guest.guestId]?.[currentEvent.id]?.attending ?? ''
                        : ''
                    }
                    onChange={(e) => {
                      if (currentEvent.id === 'welcomeParty' || currentEvent.id === 'wedding') {
                        handleAttendanceChange(guest.guestId, currentEvent.id, e.target.value === 'true');
                      }
                    }}
                    sx={{ 
                      gap: 2,
                      alignSelf: { xs: 'flex-end', sm: 'auto' },
                      width: { xs: '100%', sm: 'auto' },
                      justifyContent: { xs: 'flex-end', sm: 'flex-start' }
                    }}
                  >
                    <FormControlLabel value={true} control={<Radio />} label="Will attend" />
                    <FormControlLabel value={false} control={<Radio />} label="Can't make it" />
                  </RadioGroup>
                </FormControl>
              </Box>
              )}
              <Divider sx={{ mt: 3 }} />
            </Box>
          ))
        )}
      </Box>

      <Box sx={formMixins.formActions}>
        <Button
          variant="outlined"
          onClick={handleBack}
          sx={buttonMixins.secondaryAction}
        >
          Back
        </Button>

        <Button
          variant="contained"
          onClick={handleNext}
          disabled={currentEvent.isContactInfo && (groupRSVP.email === '' || !!emailError)}
          sx={buttonMixins.primaryAction}
        >
          {isLastStep ? 'Save RSVP' : 'Next'}
        </Button>
      </Box>
    </Box>
  );
}
